import React, { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { thirdStepConfirmValidator } from "../../../Validation/ThirdStepValidator";

import { Input, Submit, SubmitType } from "../..";
import { useLabels } from "../../../Hooks/useLabels";
import { FieldStatus } from "../../../Helpers/FieldStatus";
import { ErrorMessage } from "../../../ErrorMessage/ErrorMessage";
import { LoadingAnimation } from "../../../../LoadingAnimation/LoadingAnimation";
import { ModalLayout } from "../Modal";

interface SmsConfirmationModalFormType {
	smsCode: number;
}

interface SmsConfirmationModalProps {
	onClose: () => void;
	onSubmit: (code: number) => void;
	isOpen: boolean;
	heading: string;
	description: string;
	inputLabel: string;
	buttonLabel: string;
	loading: boolean;
	confirmationError: string;
	addResetCallback?: (callback: () => void) => void;
}

export const SmsConfirmationModal: FC<SmsConfirmationModalProps> = ({
	onClose,
	onSubmit,
	isOpen,
	heading,
	description,
	inputLabel,
	buttonLabel,
	loading,
	confirmationError,
	addResetCallback,
}) => {
	const labels = useLabels();

	const {
		handleSubmit,
		getValues,
		formState: { errors },
		register,
		reset,
	} = useForm<SmsConfirmationModalFormType>({
		mode: "onBlur",
		reValidateMode: "onChange",
		resolver: yupResolver(
			thirdStepConfirmValidator(labels.bookingLabels!.bookingErrors)
		),
	});

	useEffect(() => {
		if (addResetCallback) addResetCallback(reset);
	}, []);

	function submitCode({ smsCode }: SmsConfirmationModalFormType) {
		onSubmit(smsCode);
	}

	return (
		<ModalLayout
			className="ConfirmationModal"
			onClose={onClose}
			isOpen={isOpen}
			heading={heading}
			bgColorVariant="blue"
		>
			<div
				className="ConfirmationModal__description"
				dangerouslySetInnerHTML={{
					__html: description,
				}}
			/>
			{confirmationError && <ErrorMessage className="ConfirmationModal__errorMessage" message={confirmationError} />}
			<form
				onSubmit={handleSubmit(submitCode)}
				className="ConfirmationModal__form"
			>
				<Input
					className="ConfirmationModal__input"
					autoFocus
					error={errors.smsCode}
					getValues={getValues}
					label={inputLabel}
					name="smsCode"
					type="number"
					register={register}
					inputmode="numeric"
					autocomplete="one-time-code"
					pattern="\d{6}"
				/>
				<div className="ConfirmationModal__submit">
					<Submit
						type={SubmitType.submit}
						status={
							!!errors.smsCode || loading
								? FieldStatus.disabled
								: FieldStatus.active
						}
					>
						{loading ? <LoadingAnimation text={buttonLabel} /> : buttonLabel}
					</Submit>
				</div>
			</form>
		</ModalLayout>
	);
};
